import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Group, Image as KonvaImage, Layer, Line, Rect, Stage, Text } from 'react-konva';
import useImage from 'use-image';
import CommentBubble from './CommentBubble';
import type { BoundingBox, CanvasComment, EquipmentIsolationAction, IsolationAction, IsolationActionType } from '../types';

export interface HoverableItem {
  id: string;
  category: 'equipment' | 'equipment_instrument' | 'node_instrument' | 'untagged_instrument' | 'node';
  bbox: BoundingBox;
  label: string;
  properties: Record<string, string | undefined>;
  polyline?: number[];
}

interface IsolationCanvasProps {
  imageUrl: string;
  actions: IsolationAction[];
  equipmentIsolations?: EquipmentIsolationAction[];
  selectedActionId: string | null;
  width: number;
  height: number;
  hoverableItems?: HoverableItem[];
  comments?: CanvasComment[];
  commentMode?: boolean;
  selectedCommentId?: string | null;
  showResolvedComments?: boolean;
  reviewedInstrumentIds?: Set<string>;
  onCreateComment?: (canvasX: number, canvasY: number, screenX: number, screenY: number) => void;
  onSelectComment?: (commentId: string, screenX: number, screenY: number) => void;
}

const colorByAction: Record<IsolationActionType, string> = {
  CLOSE: '#ef4444',
  OPEN: '#3b82f6',
  NOTE: '#f59e0b',
};

const FOCUS_SCALE = 2.5;
const FOCUS_PADDING = 80;
const ANIMATE_DURATION_MS = 350;

const legendStyle: React.CSSProperties = {
  position: 'absolute',
  top: 10,
  left: 10,
  backgroundColor: 'rgba(255, 255, 255, 0.92)',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '6px 10px',
  fontSize: 11,
  color: '#111827',
  lineHeight: 1.5,
  zIndex: 2,
  pointerEvents: 'none',
};

const containerStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  backgroundColor: '#f8fafc',
};

const stageWrapStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
};

function OverlayRect({
  action,
  scale,
  isHovered,
  isSelected,
  isReviewed,
  onEnter,
  onLeave,
}: {
  action: IsolationAction;
  scale: number;
  isHovered: boolean;
  isSelected: boolean;
  isReviewed: boolean;
  onEnter: () => void;
  onLeave: () => void;
}) {
  const bbox = action.bbox!;
  // If reviewed, use green color; otherwise use the action's color
  const stroke = isReviewed ? '#22c55e' : colorByAction[action.action];
  const w = Math.max(1, bbox.x2 - bbox.x1);
  const h = Math.max(1, bbox.y2 - bbox.y1);
  const labelFontSize = Math.max(10, 12 / scale);
  const labelPad = 4 / scale;
  const labelH = labelFontSize + labelPad * 2;
  const gap = 2 / scale;

  const showLabel = isHovered || isSelected;
  const fillOpacity = isSelected ? '55' : isHovered ? '44' : '22';
  const strokeW = isSelected ? 3 / scale : 2 / scale;

  const labelText = `${action.instrument_ref} (${isReviewed ? 'REVIEWED' : action.action})`;
  // Bold Arial character width ≈ 0.62 em; add padding on both sides
  const estimatedTextW = labelText.length * labelFontSize * 0.62 + labelPad * 2;
  const labelW = Math.max(w, estimatedTextW);

  // Render label below the bbox when there isn't enough room above it
  const labelAboveY = bbox.y1 - labelH - gap;
  const labelY = labelAboveY >= 0 ? labelAboveY : bbox.y2 + gap;

  return (
    <Group onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <Rect
        x={bbox.x1}
        y={bbox.y1}
        width={w}
        height={h}
        stroke={stroke}
        strokeWidth={strokeW}
        fill={`${stroke}${fillOpacity}`}
      />
      {showLabel && (
        <>
          <Rect
            x={bbox.x1}
            y={labelY}
            width={labelW}
            height={labelH}
            fill="rgba(0,0,0,0.78)"
            cornerRadius={3 / scale}
          />
          <Text
            x={bbox.x1 + labelPad}
            y={labelY + labelPad}
            text={labelText}
            fontSize={labelFontSize}
            fill="#ffffff"
            fontStyle="bold"
          />
        </>
      )}
    </Group>
  );
}

const EQUIPMENT_ISOLATION_COLOR = '#ea580c'; // orange-600

export default function IsolationCanvas({
  imageUrl,
  actions,
  equipmentIsolations = [],
  selectedActionId,
  width,
  height,
  hoverableItems = [],
  comments = [],
  commentMode = false,
  selectedCommentId = null,
  showResolvedComments = false,
  reviewedInstrumentIds = new Set(),
  onCreateComment,
  onSelectComment,
}: IsolationCanvasProps) {
  const [image] = useImage(imageUrl, 'anonymous');
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<HoverableItem | null>(null);
  const stageRef = useRef<any>(null);
  const scaleRef = useRef(1);
  const positionRef = useRef({ x: 0, y: 0 });
  const animFrameRef = useRef<number | null>(null);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    if (!image || width <= 0 || height <= 0) {
      return;
    }

    const nextScale = Math.min(width / image.width, height / image.height);
    const centered = {
      x: (width - image.width * nextScale) / 2,
      y: (height - image.height * nextScale) / 2,
    };
    setScale(nextScale);
    setPosition(centered);
  }, [image, width, height]);

  // Animate pan/zoom to the selected action's bbox
  useEffect(() => {
    if (!selectedActionId || width <= 0 || height <= 0) return;

    const selected = actions.find((a) => a.instrument_id === selectedActionId);
    if (!selected?.bbox) return;

    const bbox = selected.bbox;
    const bboxW = Math.max(1, bbox.x2 - bbox.x1);
    const bboxH = Math.max(1, bbox.y2 - bbox.y1);
    const centerX = bbox.x1 + bboxW / 2;
    const centerY = bbox.y1 + bboxH / 2;

    const targetScale = Math.min(
      (width - FOCUS_PADDING * 2) / bboxW,
      (height - FOCUS_PADDING * 2) / bboxH,
      FOCUS_SCALE,
    );

    const targetPos = {
      x: width / 2 - centerX * targetScale,
      y: height / 2 - centerY * targetScale,
    };

    const startScale = scaleRef.current;
    const startPos = { ...positionRef.current };
    const startTime = performance.now();

    if (animFrameRef.current != null) cancelAnimationFrame(animFrameRef.current);

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / ANIMATE_DURATION_MS);
      const ease = 1 - Math.pow(1 - t, 3); // ease-out cubic

      const s = startScale + (targetScale - startScale) * ease;
      const x = startPos.x + (targetPos.x - startPos.x) * ease;
      const y = startPos.y + (targetPos.y - startPos.y) * ease;

      setScale(s);
      setPosition({ x, y });

      if (t < 1) {
        animFrameRef.current = requestAnimationFrame(animate);
      } else {
        animFrameRef.current = null;
      }
    };

    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animFrameRef.current != null) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
    };
  }, [selectedActionId, actions, width, height]);

  const handleWheel = useCallback((e: any) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage) {
      return;
    }

    const pointer = stage.getPointerPosition();
    if (!pointer) {
      return;
    }

    const currentScale = scaleRef.current;
    const currentPosition = positionRef.current;

    const mousePoint = {
      x: (pointer.x - currentPosition.x) / currentScale,
      y: (pointer.y - currentPosition.y) / currentScale,
    };

    const direction = e.evt.deltaY > 0 ? -1 : 1;
    const zoomFactor = 1.07;
    const minScale = 0.1;
    const maxScale = 10;
    const nextScale = Math.max(minScale, Math.min(maxScale, currentScale * (direction > 0 ? zoomFactor : 1 / zoomFactor)));

    const nextPosition = {
      x: pointer.x - mousePoint.x * nextScale,
      y: pointer.y - mousePoint.y * nextScale,
    };

    setScale(nextScale);
    setPosition(nextPosition);
  }, []);

  const handleStageClick = useCallback((e: any) => {
    if (!commentMode || !onCreateComment) return;

    const stage = stageRef.current;
    if (!stage) return;

    // Ignore clicks on comment bubbles (handled by their own onClick)
    const target = e.target;
    if (target !== stage && target.parent !== stage) {
      const name = target.name?.() || target.parent?.name?.() || '';
      if (name === 'commentBubble') return;
    }

    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    const currentScale = scaleRef.current;
    const currentPosition = positionRef.current;
    const canvasX = (pointer.x - currentPosition.x) / currentScale;
    const canvasY = (pointer.y - currentPosition.y) / currentScale;

    const container = stage.container();
    const rect = container.getBoundingClientRect();
    const screenX = rect.left + pointer.x;
    const screenY = rect.top + pointer.y;

    onCreateComment(canvasX, canvasY, screenX, screenY);
  }, [commentMode, onCreateComment]);

  const overlayActions = useMemo(
    () => actions.filter((action) => Boolean(action.bbox)),
    [actions],
  );

  // Split hoverables: non-isolation items get hit rects, isolation items enrich the action tooltip
  const { filteredHoverables, hoverableById } = useMemo(() => {
    const isolationIds = new Set(actions.map((a) => a.instrument_id));
    const byId = new Map<string, HoverableItem>();
    const filtered: HoverableItem[] = [];
    for (const item of hoverableItems) {
      byId.set(item.id, item);
      if (!isolationIds.has(item.id)) {
        filtered.push(item);
      }
    }
    return { filteredHoverables: filtered, hoverableById: byId };
  }, [hoverableItems, actions]);

  const visibleComments = useMemo(
    () => comments.filter((c) => showResolvedComments || !c.resolved),
    [comments, showResolvedComments],
  );

  if (width <= 0 || height <= 0) {
    return null;
  }

  return (
    <div style={{ ...containerStyle, cursor: commentMode ? 'crosshair' : 'default' }}>
      <div style={legendStyle}>
        <div><span style={{ color: colorByAction.CLOSE }}>&#9632;</span> CLOSE</div>
        <div><span style={{ color: colorByAction.OPEN }}>&#9632;</span> OPEN</div>
        <div><span style={{ color: colorByAction.NOTE }}>&#9632;</span> NOTE</div>
        <div><span style={{ color: '#22c55e' }}>&#9632;</span> REVIEWED</div>
        {equipmentIsolations.length > 0 && (
          <div><span style={{ color: EQUIPMENT_ISOLATION_COLOR }}>&#9632;</span> LOTO</div>
        )}
      </div>
      {commentMode && (
        <div style={commentBannerStyle}>
          Click anywhere on the diagram to add a comment
        </div>
      )}
      <div style={stageWrapStyle}>
        <Stage
          ref={stageRef}
          width={width}
          height={height}
          x={position.x}
          y={position.y}
          scaleX={scale}
          scaleY={scale}
          draggable={!commentMode}
          onDragMove={(e) => setPosition({ x: e.target.x(), y: e.target.y() })}
          onWheel={handleWheel}
          onClick={handleStageClick}
          onTap={handleStageClick}
        >
          <Layer>
            {image && <KonvaImage image={image} width={image.width} height={image.height} />}
            {/* Hit areas for all P&ID items — light yellow highlight on hover */}
            {filteredHoverables.map((item) => {
              const isItemHovered = hoveredItem?.id === item.id;
              const enterHandler = () => setHoveredItem(item);
              const leaveHandler = () => setHoveredItem((prev) => (prev?.id === item.id ? null : prev));

              if (item.polyline && item.polyline.length >= 4) {
                return (
                  <Line
                    key={item.id}
                    points={item.polyline}
                    stroke={isItemHovered ? '#facc15' : 'transparent'}
                    strokeWidth={Math.max(8, 10 / scale)}
                    lineCap="round"
                    lineJoin="round"
                    hitStrokeWidth={Math.max(14, 18 / scale)}
                    onMouseEnter={enterHandler}
                    onMouseLeave={leaveHandler}
                  />
                );
              }

              return (
                <Rect
                  key={item.id}
                  x={item.bbox.x1}
                  y={item.bbox.y1}
                  width={Math.max(1, item.bbox.x2 - item.bbox.x1)}
                  height={Math.max(1, item.bbox.y2 - item.bbox.y1)}
                  fill={isItemHovered ? 'rgba(250, 204, 21, 0.25)' : 'transparent'}
                  stroke={isItemHovered ? '#facc15' : undefined}
                  strokeWidth={isItemHovered ? 2 / scale : 0}
                  onMouseEnter={enterHandler}
                  onMouseLeave={leaveHandler}
                />
              );
            })}
            {overlayActions.map((action) => (
              <OverlayRect
                key={action.instrument_id}
                action={action}
                scale={scale}
                isHovered={hoveredId === action.instrument_id}
                isSelected={selectedActionId === action.instrument_id}
                isReviewed={reviewedInstrumentIds.has(action.instrument_id)}
                onEnter={() => setHoveredId(action.instrument_id)}
                onLeave={() => setHoveredId((prev) => (prev === action.instrument_id ? null : prev))}
              />
            ))}
            {/* Equipment isolation (LOTO) overlays — orange */}
            {equipmentIsolations
              .filter((eq) => eq.bbox)
              .map((eq) => {
                const bbox = eq.bbox!;
                const w = Math.max(1, bbox.x2 - bbox.x1);
                const h = Math.max(1, bbox.y2 - bbox.y1);
                const isEqHovered = hoveredId === `eq-${eq.equipment_no}`;
                const strokeW = isEqHovered ? 3 / scale : 2 / scale;
                const labelFontSize = Math.max(10, 12 / scale);
                const labelPad = 4 / scale;
                const labelH = labelFontSize + labelPad * 2;
                const gap = 2 / scale;
                const labelText = `${eq.equipment_no} — ${eq.action}`;
                const estimatedTextW = labelText.length * labelFontSize * 0.62 + labelPad * 2;
                const labelW = Math.max(w, estimatedTextW);
                const labelAboveY = bbox.y1 - labelH - gap;
                const labelY = labelAboveY >= 0 ? labelAboveY : bbox.y2 + gap;

                return (
                  <Group
                    key={`eq-${eq.equipment_no}`}
                    onMouseEnter={() => setHoveredId(`eq-${eq.equipment_no}`)}
                    onMouseLeave={() => setHoveredId((prev) => (prev === `eq-${eq.equipment_no}` ? null : prev))}
                  >
                    <Rect
                      x={bbox.x1}
                      y={bbox.y1}
                      width={w}
                      height={h}
                      stroke={EQUIPMENT_ISOLATION_COLOR}
                      strokeWidth={strokeW}
                      fill={`${EQUIPMENT_ISOLATION_COLOR}${isEqHovered ? '44' : '22'}`}
                      dash={[8 / scale, 4 / scale]}
                    />
                    {isEqHovered && (
                      <>
                        <Rect
                          x={bbox.x1}
                          y={labelY}
                          width={labelW}
                          height={labelH}
                          fill="rgba(0,0,0,0.78)"
                          cornerRadius={3 / scale}
                        />
                        <Text
                          x={bbox.x1 + labelPad}
                          y={labelY + labelPad}
                          text={labelText}
                          fontSize={labelFontSize}
                          fill="#ffffff"
                          fontStyle="bold"
                        />
                      </>
                    )}
                  </Group>
                );
              })}
            {visibleComments.map((comment) => (
              <CommentBubble
                key={comment.id}
                comment={comment}
                scale={scale}
                isSelected={selectedCommentId === comment.id}
                onClick={() => {
                  if (onSelectComment) {
                    const stage = stageRef.current;
                    const container = stage?.container();
                    if (container) {
                      const rect = container.getBoundingClientRect();
                      const currentScale = scaleRef.current;
                      const currentPosition = positionRef.current;
                      const screenX = rect.left + (comment.x * currentScale + currentPosition.x);
                      const screenY = rect.top + (comment.y * currentScale + currentPosition.y);
                      onSelectComment(comment.id, screenX, screenY);
                    }
                  }
                }}
              />
            ))}
            {/* Tooltip for hovered items */}
            {(() => {
              let tooltipBbox: BoundingBox | null = null;
              const lines: { text: string; bold?: boolean }[] = [];

              if (hoveredId) {
                // Isolation action hovered — show native instrument details + action info
                const action = overlayActions.find((a) => a.instrument_id === hoveredId);
                if (action?.bbox) {
                  tooltipBbox = action.bbox;
                  lines.push({ text: `${action.instrument_ref} (${action.action})`, bold: true });
                  // Show native P&ID properties first (actual type, subtype, etc.)
                  const nativeItem = hoverableById.get(hoveredId);
                  if (nativeItem) {
                    for (const [key, value] of Object.entries(nativeItem.properties)) {
                      if (value) lines.push({ text: `${key}: ${value}` });
                    }
                  }
                  // action.instrument_type is actually the isolation code (IV, DV, etc.), not the instrument type
                  lines.push({ text: `Isolation Code: ${action.instrument_type}` });
                  if (action.line_no && !nativeItem?.properties['Line']) {
                    lines.push({ text: `Line: ${action.line_no}` });
                  }
                  if (action.reasoning) lines.push({ text: `Reason: ${action.reasoning}` });
                }
              } else if (hoveredItem) {
                // Non-isolation item hovered
                tooltipBbox = hoveredItem.bbox;
                lines.push({ text: hoveredItem.label, bold: true });
                lines.push({ text: `Category: ${hoveredItem.category.replace(/_/g, ' ')}` });
                for (const [key, value] of Object.entries(hoveredItem.properties)) {
                  if (value) lines.push({ text: `${key}: ${value}` });
                }
              }

              if (!tooltipBbox || lines.length === 0) return null;

              const fontSize = Math.max(10, 11 / scale);
              const pad = 5 / scale;
              const lineH = fontSize * 1.35;
              const gap = 3 / scale;

              const tooltipH = pad * 2 + lines.length * lineH;
              const maxTextLen = Math.max(...lines.map((l) => l.text.length));
              const tooltipW = pad * 2 + maxTextLen * fontSize * 0.58;

              const tooltipAboveY = tooltipBbox.y1 - tooltipH - gap;
              const tooltipY = tooltipAboveY >= 0 ? tooltipAboveY : tooltipBbox.y2 + gap;
              const tooltipX = tooltipBbox.x1;

              return (
                <Group>
                  <Rect
                    x={tooltipX}
                    y={tooltipY}
                    width={tooltipW}
                    height={tooltipH}
                    fill="rgba(17, 24, 39, 0.92)"
                    cornerRadius={4 / scale}
                    shadowColor="rgba(0,0,0,0.3)"
                    shadowBlur={6 / scale}
                    shadowOffsetY={2 / scale}
                  />
                  {lines.map((line, i) => (
                    <Text
                      key={i}
                      x={tooltipX + pad}
                      y={tooltipY + pad + i * lineH}
                      text={line.text}
                      fontSize={fontSize}
                      fill="#ffffff"
                      fontStyle={line.bold ? 'bold' : 'normal'}
                    />
                  ))}
                </Group>
              );
            })()}
          </Layer>
        </Stage>
      </div>
    </div>
  );
}

const commentBannerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 10,
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: 'rgba(245, 158, 11, 0.92)',
  color: '#fff',
  borderRadius: 6,
  padding: '4px 12px',
  fontSize: 12,
  fontWeight: 500,
  zIndex: 3,
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
};
